import { Helmet } from 'react-helmet-async';

/**
 * SEO Component
 * 
 * Provides consistent meta tags across all pages for better SEO and social sharing.
 * 
 * Usage:
 * <SEO 
 *   title="Page Title"
 *   description="Page description"
 *   canonical="/page-path"
 *   type="website"
 * />
 */

const SEO = ({ 
  title, 
  description, 
  canonical, 
  type = 'website',
  image,
  noindex = false,
  structuredData
}) => {
  const siteUrl = 'https://calcbuildai.com';
  const siteName = 'CalcBuildAI';
  const defaultDescription = 'Calculate construction materials instantly with AI. Get accurate estimates for concrete, drywall, paint, flooring, lumber, and roofing. Smart estimates for smarter builds.';
  const defaultImage = `${siteUrl}/og-image.png`;
  
  const pageTitle = title 
    ? `${title} - ${siteName}` 
    : `${siteName} - AI-Powered Construction Material Calculator`;
  
  const pageDescription = description || defaultDescription;
  const pageUrl = canonical ? `${siteUrl}${canonical}` : siteUrl;
  const pageImage = image || defaultImage;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="title" content={pageTitle} />
      <meta name="description" content={pageDescription} />
      
      {/* Robots */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow" />
      )}
      
      {/* Canonical URL */}
      <link rel="canonical" href={pageUrl} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:site_name" content={siteName} />
      
      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      
      {/* Structured Data */}
      {structuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
    </Helmet>
  );
};

/**
 * Pre-built structured data for common page types
 */
export const getCalculatorSchema = (calculatorType) => {
  const calculatorNames = {
    concrete: 'Concrete Calculator',
    drywall: 'Drywall Calculator',
    paint: 'Paint Calculator',
    flooring: 'Flooring Calculator',
    lumber: 'Lumber Calculator',
    roofing: 'Roofing Calculator',
  };
  
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": `CalcBuildAI ${calculatorNames[calculatorType] || 'Calculator'}`,
    "description": `AI-powered ${calculatorType} material calculator for accurate construction estimates`,
    "url": `https://calcbuildai.com/calculators/${calculatorType}`,
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    }
  };
};

export const getFAQSchema = (faqs) => {
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };
};

export const getBreadcrumbSchema = (items) => {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `https://calcbuildai.com${item.path}`
    }))
  };
};

export const getOrganizationSchema = () => {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "CalcBuildAI",
    "url": "https://calcbuildai.com",
    "logo": "https://calcbuildai.com/logo512.png",
    "description": "AI-powered construction material calculator for accurate estimates",
    "contactPoint": {
      "@type": "ContactPoint",
      "email": "support@calcbuildai.com",
      "contactType": "customer service"
    }
  };
};

export default SEO;
